/**
 * Topbar (file kept as Header.jsx so existing imports don't need updating).
 *
 * Layout (full viewport width):
 *
 *   [hamburger*] [LOGO]    [    SEARCH centered    ]    [workspaces] [notifs] [profile]
 *      *mobile only           horizontally centered
 *
 * Sits on the canvas (frame) color with NO own background and NO border —
 * sidebar below shares the same canvas color so the two flow seamlessly.
 *
 * Reuses existing OvacsNotificationBell and WorkspaceDropdown. All action
 * buttons share a unified `h-9` height so they sit on a single optical
 * baseline regardless of which icon set / chevron they contain.
 */

import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { HiOutlineBars3, HiOutlineChevronDown } from 'react-icons/hi2'
import { useAuth } from '../../contexts/AuthContext'
import { useSidebar } from '../../contexts/SidebarContext'
import OvacsNotificationBell from '../notifications/OvacsNotificationBell'
import WorkspaceDropdown from '../workspace/WorkspaceDropdown'
import SearchBar from './SearchBar'
import ProfileDropdown from './ProfileDropdown'
import ovacsLogo from '../../assets/images/logos/ovacs-logo.svg'
import ovacsIcon from '../../assets/images/icons/Icon.svg'

const Header = ({ onMenuClick }) => {
  const { t } = useTranslation()
  const { isWorkspaceSwitched } = useAuth()
  const { collapsed } = useSidebar()
  const [showWorkspace, setShowWorkspace] = useState(false)
  const workspaceRef = useRef(null)
  const navigate = useNavigate()

  // Close Workspaces dropdown on click-outside or Esc — matches the
  // behavior of OvacsNotificationBell + ProfileDropdown.
  useEffect(() => {
    if (!showWorkspace) return undefined
    const onClick = (e) => {
      if (workspaceRef.current && !workspaceRef.current.contains(e.target)) {
        setShowWorkspace(false)
      }
    }
    const onKey = (e) => { if (e.key === 'Escape') setShowWorkspace(false) }
    document.addEventListener('mousedown', onClick)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onClick)
      document.removeEventListener('keydown', onKey)
    }
  }, [showWorkspace])

  return (
    <header className="h-12 flex items-center gap-2 px-3 sm:px-4 bg-canvas">
      {/* Start cluster — hamburger + logo. Width matches sidebar so SEARCH
          stays optically centered. The logo cross-fades between the full
          wordmark (sidebar open) and the icon-only mark (sidebar collapsed). */}
      <div
        className={`flex items-center gap-2 min-w-0 flex-shrink-0 transition-[width] duration-200 ease-out ${
          collapsed ? 'lg:w-14' : 'lg:w-60'
        }`}
      >
        <button
          type="button"
          onClick={onMenuClick}
          className="lg:hidden h-9 w-9 inline-flex items-center justify-center rounded-lg text-ink-muted hover:text-ink-primary hover:bg-surface transition-colors flex-shrink-0"
          aria-label={t('common.menu', 'Open menu')}
        >
          <HiOutlineBars3 className="w-5 h-5" />
        </button>

        <button
          type="button"
          onClick={() => navigate('/dashboard')}
          className="h-9 inline-flex items-center justify-center px-2 rounded-lg hover:bg-surface transition-colors flex-shrink-0"
          title={t('common.appName', 'OVACS')}
          aria-label={t('common.appName', 'OVACS')}
        >
          {/* Crossfade container — both images stacked, opacity + scale toggled.
              Width animates so the start-cluster smoothly tracks the sidebar. */}
          <span
            className="relative inline-flex items-center justify-center h-7 transition-[width] duration-300 ease-out"
            style={{ width: collapsed ? '1.75rem' : '7rem' }}
          >
            <img
              src={ovacsLogo}
              alt="OVACS"
              className={`absolute inset-0 m-auto h-7 w-auto object-contain transition-all duration-300 ease-out ${
                collapsed ? 'opacity-0 scale-90 pointer-events-none' : 'opacity-100 scale-100'
              }`}
            />
            <img
              src={ovacsIcon}
              alt=""
              aria-hidden="true"
              className={`absolute inset-0 m-auto h-7 w-7 object-contain transition-all duration-300 ease-out ${
                collapsed ? 'opacity-100 scale-100' : 'opacity-0 scale-90 pointer-events-none'
              }`}
            />
          </span>
        </button>
      </div>

      {/* Center — search field, centered horizontally in the topbar */}
      <div className="flex-1 flex justify-center min-w-0">
        <SearchBar className="w-full max-w-xl" />
      </div>

      {/* End cluster — workspaces / notifications / profile. Same target
          width as the start cluster so search stays visually centered.
          All buttons use h-9 so the chevrons + bell + avatar sit on
          one optical baseline. */}
      <div className="flex items-center gap-1 justify-end lg:w-60 lg:flex-shrink-0">
        {!isWorkspaceSwitched && (
          <div ref={workspaceRef} className="relative hidden md:block">
            <button
              type="button"
              onClick={() => setShowWorkspace(v => !v)}
              aria-haspopup="menu"
              aria-expanded={showWorkspace}
              className="h-9 inline-flex items-center gap-1.5 px-2.5 text-xs font-medium text-ink-secondary hover:text-ink-primary hover:bg-surface rounded-lg transition-colors"
            >
              <span>{t('workspace.workspaces')}</span>
              <HiOutlineChevronDown className={`w-3.5 h-3.5 transition-transform ${showWorkspace ? 'rotate-180' : ''}`} />
            </button>
            {showWorkspace && (
              <WorkspaceDropdown onClose={() => setShowWorkspace(false)} />
            )}
          </div>
        )}

        <OvacsNotificationBell />

        <ProfileDropdown />
      </div>
    </header>
  )
}

export default Header
